// Components/Services/TakeoverProjects/WhoItsFor.jsx
import React from "react";
import { motion } from "framer-motion";
import { FiHome, FiTrendingUp, FiShield, FiUsers, FiBriefcase, FiTool } from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const audience = [
  {
    icon: <FiHome className="w-5 h-5 text-[#212529]" />,
    title: "Homeowners with Failed Contractors",
    desc: "Your contractor abandoned the job, took your deposit, or produced work that failed inspection. We assess the damage and take it from there.",
  },
  {
    icon: <FiTrendingUp className="w-5 h-5 text-[#212529]" />,
    title: "Real Estate Investors",
    desc: "Investors with stalled flips, over-budget rehabs, or properties stuck mid-renovation that need a reliable contractor to complete the job and protect their ROI.",
  },
  {
    icon: <FiShield className="w-5 h-5 text-[#212529]" />,
    title: "Construction Attorneys",
    desc: "Legal professionals handling construction defect litigation who need a licensed contractor to assess, document, and remediate defective work with court-ready reporting.",
  },
  {
    icon: <FiUsers className="w-5 h-5 text-[#212529]" />,
    title: "Insurance Claims Clients",
    desc: "Homeowners dealing with construction-related insurance claims who need a licensed contractor to assess damage, provide estimates, and perform covered repairs.",
  },
  {
    icon: <FiBriefcase className="w-5 h-5 text-[#212529]" />,
    title: "Developers with Troubled Projects",
    desc: "Developers facing subcontractor failures, permitting delays, or quality control issues on active projects who need experienced oversight to get back on schedule.",
  },
  {
    icon: <FiTool className="w-5 h-5 text-[#212529]" />,
    title: "DIY Projects Gone Wrong",
    desc: "Homeowners who started a renovation themselves or hired unqualified help and now need a licensed contractor to correct the work and bring it up to code.",
  },
];

const WhoItsFor = () => {
  return (
    <section className="bg-white py-16 px-6">
      <div className="max-w-6xl mx-auto">

        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p variants={fadeUp} className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-3">
            Is This Right For You?
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-black text-[#212529] leading-tight mb-4">
            Who We Help With Takeover Projects
          </motion.h2>
          <motion.p variants={fadeUp} className="text-[#495057] text-base leading-relaxed max-w-2xl mx-auto">
            If your project is stuck, broken, or in legal dispute — we have the experience, licensing, and documentation standards to step in and fix it.
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
        >
          {audience.map((item, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              whileHover={{ x: 4, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="flex items-start gap-4 bg-[#f8f9fa] rounded-xl px-5 py-5 border border-[#dee2e6] shadow-sm cursor-default"
            >
              <div className="w-10 h-10 rounded-full bg-white border border-[#dee2e6] flex items-center justify-center flex-shrink-0 shadow-sm">
                {item.icon}
              </div>
              <div>
                <p className="text-[#212529] font-bold text-sm">{item.title}</p>
                <p className="text-[#6c757d] text-xs mt-1 leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-12 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <p className="text-[#495057] text-sm mb-4">Not sure if we can help with your situation? Call us — we'll give you a straight answer.</p>
          <a
            href="/contact"
            className="inline-block bg-[#212529] hover:bg-[#343a40] text-white text-sm font-semibold uppercase tracking-wider px-8 py-3 rounded-full transition duration-300"
          >
            Talk to Our Team →
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default WhoItsFor;